import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Clock, BarChart3, ArrowRight, TrendingUp, Users, PiggyBank, Target,
  Database, Layers, PieChart, Brain, Calculator, Lightbulb, Award
} from "lucide-react";
import { COURSES, LD_OUTCOMES } from "../mock";
import { CATEGORY_PAGES } from "../data/categoryPages";

const OUT_ICONS = { TrendingUp, Users, PiggyBank, Target };

const PROGRAM_MODULES = [
  { title: "Data Analytics", icon: BarChart3, desc: "Develop practical data analytics skills to collect, clean, analyze, and interpret business data for informed decision-making using modern analytical techniques." },
  { title: "Data Science", icon: Database, desc: "Explore the complete data science lifecycle, from data preparation and statistical analysis to predictive modeling and business insights using industry-standard tools." },
  { title: "Big Data Analytics", icon: Layers, desc: "Analyze large-scale structured and unstructured datasets using big data technologies to uncover patterns, improve operational efficiency, and support enterprise decision-making." },
  { title: "Data Visualization", icon: PieChart, desc: "Create interactive dashboards and compelling visual reports using leading BI and visualization tools to communicate insights with clarity and impact." },
  { title: "Machine Learning & AI", icon: Brain, desc: "Learn supervised and unsupervised machine learning techniques, predictive analytics, feature engineering, and model evaluation for enterprise AI applications." },
  { title: "Statistics for AI & ML", icon: Calculator, desc: "Master statistical concepts including probability, hypothesis testing, regression, distributions, and data interpretation that form the foundation of AI and machine learning." },
  { title: "Data Interpretation & Business Insights", icon: Lightbulb, desc: "Convert raw data into meaningful business insights through exploratory analysis, KPI measurement, trend identification, and evidence-based decision-making." },
  { title: "Capstone Projects", icon: Award, desc: "Apply newly acquired knowledge through enterprise-focused capstone projects, working with real-world datasets to solve practical analytics and AI challenges." }
];

const CategoryPage = () => {
  const { slug } = useParams();
  const page = CATEGORY_PAGES[slug];

  if (!page) return <Navigate to="/offerings/all" replace/>;

  const courses = COURSES.filter(c => page.catKeys.includes(c.catKey));

  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE LEARNING SOLUTIONS"
        title="Future-Ready Workforces, Built for L&D Heads"
        subtitle="A consolidated talent partner for India's most ambitious enterprises — helping you close skill gaps, accelerate transformation and measure ROI on every learning rupee."
      />

      {/* Outcome metrics — common across all Skills Programs pages */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">MEASURABLE OUTCOMES</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Built around the metrics your CFO cares about.</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LD_OUTCOMES.map((o, i) => {
            const Icon = OUT_ICONS[o.icon] || TrendingUp;
            return (
              <Reveal key={o.label} variant="up" delay={i * 80}>
                <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-[#6BBE32]"/>
                  </div>
                  <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                  <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Program modules — 2 rows of 4 cards, common across all Skills Programs pages */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">PROGRAM MODULES</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">What you'll learn</h2>
            <p className="mt-3 text-gray-600">A structured curriculum covering the core skills enterprises need most.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROGRAM_MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
                <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                    <Icon size={20} className="text-white"/>
                  </div>
                  <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16">
        {courses.length > 0 ? (
          <>
            <Reveal>
              <div className="text-center max-w-2xl mx-auto mb-10">
                <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">FEATURED COURSES</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Explore {page.label} courses</h2>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((c, i) => (
                <Reveal key={c.id} variant="up" delay={i * 60}>
                  <div className="sg-card group bg-white border border-gray-100 rounded-2xl overflow-hidden h-full flex flex-col">
                    <img src={c.image} alt={c.title} className="w-full h-40 object-cover"/>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-xs font-semibold text-[#6BBE32] mb-1">{c.body}</div>
                      <h4 className="font-bold text-[#15348C] leading-snug">{c.title}</h4>
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed flex-1">{c.desc}</p>
                      <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Clock size={14}/>{c.duration}</span>
                        <span className="flex items-center gap-1"><BarChart3 size={14}/>{c.level}</span>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                        <span className="font-bold text-[#15348C]">{c.price}</span>
                        <Link to="/contact" className="text-[#4F9622] font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">
                          Enquire <ArrowRight size={14}/>
                        </Link>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </>
        ) : (
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">COMING SOON</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">{page.label} courses are on the way</h2>
              <p className="mt-3 text-gray-600">We're onboarding new {page.label.toLowerCase()} programs. Talk to our advisor for early access or a custom curriculum.</p>
            </div>
          </Reveal>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden text-center">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#8BD835]/20 rounded-full blur-3xl"/>
            <div className="relative">
              <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Get a tailored {page.label} training proposal.</h2>
              <p className="mt-3 text-gray-300 max-w-xl mx-auto">Talk to a SkillsGain advisor about your team's goals, timelines and budget.</p>
              <Link to="/contact" className="sg-btn-glow inline-block mt-6 bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3 rounded-md transition-colors">
                Talk to Our Advisor
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default CategoryPage;
